import { ReactNode, useEffect, useRef } from 'react';
import { Theme, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Box } from '@mui/system';
import { useDrawerContext } from '../contexts';

interface ILayoutBaseDePaginaProps {
    // Sem título (ex.: Faturamento.tsx, que já mostra o nome da página dentro
    // do próprio card de "sessão ativa" no topo da barraDeFerramentas), a
    // linha inteira do cabeçalho nem é renderizada — reaproveita esse espaço
    // vertical pra listagem, em vez de reservar uma altura fixa vazia.
    titulo?: string;
    // Linha curta abaixo do título (guia TORQ, padrão de listagem) — opcional,
    // páginas que não passam continuam com o cabeçalho de sempre.
    subtitulo?: string;
    children: React.ReactNode
    barraDeFerramentas?: ReactNode;
    // Ação em destaque (ex.: botão "Novo X") alinhada à direita do título,
    // em vez de dentro da barra de ferramentas — mesmo padrão do mockup.
    acao?: ReactNode;
}

export const LayoutBaseDePagina: React.FC<ILayoutBaseDePaginaProps> = ({ children, titulo, subtitulo, barraDeFerramentas, acao }) => {
  // CORREÇÃO: Usar breakpoints diferentes para cada variável
  const smDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm')); // < 600px
  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md')); // < 900px
  // Janelas largas porém baixas (ex.: navegador não maximizado) não são cobertas pelos
  // breakpoints de largura acima — sem isso o título reserva uma altura fixa grande demais
  // e sobra pouco espaço vertical para a listagem/tabela.
  const shortH = useMediaQuery('(max-height:760px)');
  const theme = useTheme();

  // CORREÇÃO: Consumir mais estados do contexto
  const { isDrawerOpen } = useDrawerContext();

  // Refs para armazenar os valores anteriores
  const prevSmDown = useRef(smDown);
  const prevMdDown = useRef(mdDown);

  // Console para monitorar as mudanças de estado específicas
  useEffect(() => {
    // Verifica se smDown mudou
    if (prevSmDown.current !== smDown) {
      prevSmDown.current = smDown;
    }

    // Verifica se mdDown mudou
    if (prevMdDown.current !== mdDown) {
      prevMdDown.current = mdDown;
    }

   
  }, [smDown, mdDown, isDrawerOpen]);



  return (
    <Box height="100%" display="flex" flexDirection="column">
      {titulo && (
        <Box
          // no celular as margens são menores — cada pixel de largura conta
          marginLeft={smDown ? 2 : 3}
          marginRight={smDown ? 2 : 3}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          rowGap={1}
          columnGap={2}
          // Com subtítulo a altura fixa não sobra espaço pra segunda linha —
          // vira padding vertical (auto-height) só nesse caso, pra não regredir
          // a altura das páginas que não usam subtítulo. `minHeight` (não
          // `height`) porque em telas estreitas o botão de ação pode quebrar
          // pra uma segunda linha (ver `flexWrap`) e precisa de espaço extra.
          minHeight={subtitulo ? undefined : theme.spacing(smDown ? 6 : mdDown ? 8 : shortH ? 8 : 12)}
          py={subtitulo ? (smDown ? 1.5 : 3) : undefined}
        >
          <Box display="flex" alignItems="center" gap={1} minWidth={0}>
            {/* no celular não tem botão de menu: a navegação é a barra de baixo (AppMobile.tsx) */}
            <Box minWidth={0}>
              <Typography
                overflow="hidden"
                whiteSpace="nowrap"
                textOverflow="ellipsis"
                // CORREÇÃO: Lógica corrigida para variantes de título
                variant={smDown ? 'h5' : mdDown ? 'h4' : shortH ? 'h4' : 'h3'}
                sx={smDown ? { fontWeight: 800, fontSize: '1.6rem', letterSpacing: '-0.02em' } : undefined}
              >
                {titulo}
              </Typography>
              {subtitulo && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  overflow="hidden"
                  whiteSpace={smDown ? 'normal' : 'nowrap'}
                  textOverflow="ellipsis"
                  sx={{
                    mt: 0.25,
                    ...(smDown && { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', fontSize: '0.8rem', lineHeight: 1.35 }),
                  }}
                >
                  {subtitulo}
                </Typography>
              )}
            </Box>
          </Box>

          {acao && (
            <Box flexShrink={0} sx={{ ml: 'auto', ...(smDown && { width: '100%', display: 'flex', justifyContent: 'flex-end' }) }}>
              {acao}
            </Box>
          )}
        </Box>
      )}
      {/* Sem título no celular: só um respiro no topo (a navegação é a barra de baixo). */}
      {!titulo && smDown && <Box height={8} />}

      {barraDeFerramentas && (
        <Box>
          {barraDeFerramentas}
        </Box>
      )}

      <Box marginX={smDown ? 0 : 1} marginBlockEnd={smDown ? 3 : 2}>
        {children}
      </Box>
    </Box>
  );
};